import React, { useMemo, useCallback, useState, memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import _ from "lodash";
import { Checkbox, Avatar, SearchInput } from "@toddle-edu/ds-web";
import { useI18n } from "Hooks";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";
import SchoolListSkeleton from "./SchoolListSkeleton";
import classes from "./SchoolList.scss";

const ALL_SCHOOLS_KEY = "ALL_SCHOOLS";
const SEARCH_THRESHOLD = 5;

const SchoolRow = memo(({ school, isSelected, onSelect, testId }) => {
  const label = _.get(school, "label", "");
  const logo = _.get(school, "logo");
  const schoolId = _.get(school, "id");

  const handleClick = useCallback(() => {
    onSelect(schoolId);
  }, [onSelect, schoolId]);

  return (
    <div
      className={classes.schoolRow}
      onClick={handleClick}
      onKeyDown={e => onEnterOrSpaceKeyDown(e, handleClick)}
      role="button"
      tabIndex={0}
      data-test-id={`${testId}-schoolList-${schoolId}-checkbox`}
      aria-label={label}
    >
      <div className={classes.schoolRowLeft}>
        <Avatar
          dsVersion="2.0"
          src={logo}
          name={label}
          shape="square"
          size="x-small"
        />
        <span className={classNames("text-body", classes.schoolName)}>
          {label}
        </span>
      </div>
      <Checkbox isChecked={isSelected} testId={`${testId}-${schoolId}`} />
    </div>
  );
});

SchoolRow.displayName = "SchoolRow";

const SchoolList = ({
  schools,
  selectedSchoolIds,
  onSchoolChange,
  isAllSchoolsSelected,
  hierarchyLoading,
  testId,
}) => {
  const { t } = useI18n(["common", "curriculumMaps"]);

  const [searchText, setSearchText] = useState("");

  const filteredSchools = useMemo(() => {
    if (!searchText) return schools;
    const lowerSearch = searchText.toLowerCase();
    return _.filter(schools, school =>
      _.get(school, "label", "").toLowerCase().includes(lowerSearch)
    );
  }, [schools, searchText]);

  const handleSearchChange = useCallback(value => {
    setSearchText(value);
  }, []);

  const handleSchoolSelect = useCallback(
    schoolId => {
      onSchoolChange(schoolId);
    },
    [onSchoolChange]
  );

  const handleAllSchoolsSelect = useCallback(() => {
    onSchoolChange(ALL_SCHOOLS_KEY);
  }, [onSchoolChange]);

  if (hierarchyLoading) {
    return <SchoolListSkeleton />;
  }

  const showSearch = _.size(schools) > SEARCH_THRESHOLD;
  const isAllIndeterminate =
    !isAllSchoolsSelected &&
    selectedSchoolIds instanceof Set &&
    selectedSchoolIds.size > 0;

  return (
    <div className={classes.container}>
      {showSearch && (
        <div className={classes.searchContainer}>
          <SearchInput
            dsVersion="2.0"
            placeholder={t("common:search")}
            value={searchText}
            onChange={handleSearchChange}
            size="medium"
            testId={`${testId}-schoolList-search`}
          />
        </div>
      )}
      <div className={classes.schoolListContainer}>
        {_.size(schools) > 1 && (
          <div
            className={classes.schoolRow}
            onClick={handleAllSchoolsSelect}
            onKeyDown={e => onEnterOrSpaceKeyDown(e, handleAllSchoolsSelect)}
            role="button"
            tabIndex={0}
            data-test-id={`${testId}-schoolList-allSchools-checkbox`}
            aria-label={t("curriculumMaps:all_schools")}
          >
            <div className={classes.schoolRowLeft}>
              <Avatar
                dsVersion="2.0"
                name={t("curriculumMaps:all_schools")}
                shape="square"
                size="x-small"
              />
              <div className={classes.schoolInfo}>
                <span className={classNames("text-body", classes.schoolName)}>
                  {t("curriculumMaps:all_schools")}
                </span>
              </div>
            </div>
            <Checkbox
              isChecked={isAllSchoolsSelected}
              isIndeterminate={isAllIndeterminate}
              testId={`${testId}-allSchools`}
            />
          </div>
        )}
        {_.map(filteredSchools, school => (
          <SchoolRow
            key={school.id}
            school={school}
            isSelected={
              isAllSchoolsSelected ||
              (selectedSchoolIds instanceof Set &&
                selectedSchoolIds.size > 0 &&
                selectedSchoolIds.has(school.id))
            }
            onSelect={handleSchoolSelect}
            testId={testId}
          />
        ))}
      </div>
    </div>
  );
};

SchoolList.propTypes = {
  schools: PropTypes.array,
  selectedSchoolIds: PropTypes.instanceOf(Set),
  onSchoolChange: PropTypes.func,
  isAllSchoolsSelected: PropTypes.bool,
  countsLoading: PropTypes.bool,
  hierarchyLoading: PropTypes.bool,
  testId: PropTypes.string,
};

SchoolList.defaultProps = {
  schools: [],
  selectedSchoolIds: null,
  onSchoolChange: () => {},
  isAllSchoolsSelected: true,
  allSchoolsCount: {},
  countsLoading: false,
  hierarchyLoading: false,
  selectedUserTypes: [],
  testId: "",
};

export default SchoolList;
